// Helper murni perhitungan DP (mig 070).
// products.dp_mode menentukan makna products.dp_amount:
//   • 'order'   — Rp flat per order (perilaku lama, default).
//   • 'per_pcs' — Rp per pcs → total = dp_amount × Σqty.
//   • 'percent' — % dari harga → total = round(Σ(qty × unit_price) × dp_amount / 100).
//     Item tanpa harga (unit_price null, "harga menyusul") berkontribusi 0.
// Sumber otoritatif di server = SQL compute_order_dp_total (mig 070);
// helper ini dipakai UI agar tampilan konsisten dengan server.

export type DpMode = "order" | "per_pcs" | "percent";

export type DpLineInput = {
  qty: number;
  unitPrice?: number | null;
  /**
   * Pcs baris ini yang SUDAH DITAGIH di tagihan barang hidup (mig 8000) —
   * hanya dibaca {@link computeOrderDpDue}; `computeOrderDpTotal` mengabaikannya.
   */
  billedQty?: number | null;
};

/** Total DP satu order menurut mode produk. Selalu ≥ 0. */
export function computeOrderDpTotal(
  mode: DpMode | null | undefined,
  dpValue: number | null | undefined,
  lines: ReadonlyArray<DpLineInput>
): number {
  const value = Number.isFinite(dpValue ?? NaN) ? Math.max(0, dpValue as number) : 0;
  if (value <= 0) return 0;

  switch (mode) {
    case "per_pcs": {
      const totalQty = lines.reduce(
        (s, l) => s + (Number.isFinite(l.qty) ? Math.max(0, l.qty) : 0),
        0
      );
      return value * totalQty;
    }
    case "percent": {
      const subtotal = lines.reduce(
        (s, l) =>
          s +
          (Number.isFinite(l.qty) ? Math.max(0, l.qty) : 0) *
            (l.unitPrice != null && Number.isFinite(l.unitPrice)
              ? Math.max(0, l.unitPrice)
              : 0),
        0
      );
      return Math.round((subtotal * value) / 100);
    }
    default:
      // 'order' / nilai tak dikenal → Rp flat per order (back-compat).
      return value;
  }
}

/**
 * DP yang MASIH DITAGIH satu pesanan (mig 8000, cermin SQL `compute_order_dp_due`):
 * {@link computeOrderDpTotal} atas pcs yang BELUM masuk tagihan barang hidup —
 * per baris `qty − billedQty` (tak pernah negatif). Flat per pesanan: nominal
 * utuh selama masih ada sisa, 0 bila semua pcs sudah ditagih. Tanpa `billedQty`
 * hasilnya sama dengan `computeOrderDpTotal`, kecuali flat tanpa baris sama sekali.
 */
export function computeOrderDpDue(
  mode: DpMode | null | undefined,
  dpValue: number | null | undefined,
  lines: ReadonlyArray<DpLineInput>
): number {
  const open = lines.map((l) => {
    const qty = Number.isFinite(l.qty) ? Math.max(0, l.qty) : 0;
    const billed =
      l.billedQty != null && Number.isFinite(l.billedQty) ? Math.max(0, l.billedQty) : 0;
    return { qty: Math.max(0, qty - billed), unitPrice: l.unitPrice };
  });
  if (mode !== "per_pcs" && mode !== "percent") {
    const anyOpen = open.some((l) => l.qty > 0);
    return anyOpen ? computeOrderDpTotal(mode, dpValue, open) : 0;
  }
  return computeOrderDpTotal(mode, dpValue, open);
}

// ─── DP lunas per baris pesanan (badge "DP Lunas Rp …" keranjang) ───────────
//
// Nota DP hanya menyimpan SATU baris total per pesanan, jadi nilai DP per baris
// varian tak pernah tersimpan — ia DITURUNKAN di sini dari setelan DP produk
// (putusan pemilik 2026-10-29: badge tiap baris memajang nilai barisnya, bukan
// total tagihan DP). Yang dibagi adalah uang yang BENAR-BENAR diterima nota DP
// (`dpInvoiceSettledAmount`), bukan hitungan ulang dari setelan: DP yang dikunci
// sebesar yang dibayar atau direfund sebagian tetap terbagi utuh, dan jumlah
// semua baris selalu sama persis dengan uang di tagihan DP.

/** Satu baris pesanan untuk {@link allocateDpPaidByRow}. */
export type DpRowShareInput = {
  id: string;
  qty: number;
  /** order_items.unit_price — null = harga menyusul. */
  unitPrice?: number | null;
  /** order_items.keep_status — 'cancelled' tak ikut dibagi. */
  keepStatus?: string | null;
  /** order_items.gift_role — 'hadiah' tak ber-DP (mig 8680, H11). */
  giftRole?: string | null;
};

/**
 * Bagi nominal DP satu pesanan ke baris-barisnya — uang DP lunas (badge
 * keranjang) atau DP yang ditagihkan (kartu Rincian DP & teks WhatsApp-nya,
 * `buildDpBreakdown`). Bobot per baris mengikuti
 * `dp_mode` produk, cermin `compute_order_dp_total`:
 *   • per_pcs → pcs baris;
 *   • percent → pcs × harga (baris harga menyusul berbobot 0, seperti di
 *     server); bila SEMUA baris belum berharga → per pcs;
 *   • order (rata per pesanan) / tak dikenal → per pcs: server tak punya aturan
 *     per baris untuk mode ini, jadi dibagi rata per pcs.
 * Baris batal, hadiah, dan ber-qty 0 tak ikut (tak ada di peta). Pembulatan ke
 * rupiah utuh memakai sisa terbesar — sisa rupiah jatuh ke baris berpecahan
 * terbesar (seri → baris lebih awal), jadi Σ nilai = uang yang diterima.
 * Uang ≤ 0 / tanpa baris berbobot → peta kosong (badge tanpa nominal).
 */
export function allocateDpPaidByRow(
  mode: DpMode | null | undefined,
  paidAmount: number | null | undefined,
  rows: ReadonlyArray<DpRowShareInput>
): Map<string, number> {
  const out = new Map<string, number>();
  const total =
    paidAmount != null && Number.isFinite(paidAmount) ? Math.round(paidAmount) : 0;
  if (total <= 0) return out;

  const eligible = rows.filter(
    (r) =>
      r.keepStatus !== "cancelled" &&
      r.giftRole !== "hadiah" &&
      Number.isFinite(r.qty) &&
      r.qty > 0
  );
  const byQty = eligible.map((r) => r.qty);
  let weights = byQty;
  if (mode === "percent") {
    const byPrice = eligible.map(
      (r) =>
        r.qty *
        (r.unitPrice != null && Number.isFinite(r.unitPrice) ? Math.max(0, r.unitPrice) : 0)
    );
    if (byPrice.some((w) => w > 0)) weights = byPrice;
  }
  const split = splitByWeights(total, weights);
  eligible.forEach((r, i) => {
    if (weights[i] > 0) out.set(r.id, split[i]);
  });
  return out;
}

/**
 * Bagi `total` rupiah utuh menurut bobot, pembulatan sisa terbesar: tiap bagian
 * = lantai bagiannya, lalu sisa rupiah jatuh satu per satu ke bagian berpecahan
 * terbesar (seri → indeks lebih awal). Σ hasil = `total` bila ada bobot > 0;
 * bobot ≤ 0 selalu 0. Dipakai {@link allocateDpPaidByRow} dan rincian DP per
 * baris Rekap Barang WA (`cartRecap.ts`) — satu aturan pembulatan.
 */
export function splitByWeights(total: number, weights: ReadonlyArray<number>): number[] {
  const amount = Number.isFinite(total) ? Math.max(0, Math.round(total)) : 0;
  const w = weights.map((x) => (Number.isFinite(x) && x > 0 ? x : 0));
  const sumW = w.reduce((s, x) => s + x, 0);
  if (amount <= 0 || sumW <= 0) return w.map(() => 0);

  const parts = w.map((x, i) => {
    const exact = (amount * x) / sumW;
    const floor = Math.floor(exact);
    return { i, value: floor, frac: exact - floor, weight: x };
  });
  let rest = amount - parts.reduce((s, p) => s + p.value, 0);
  const order = parts
    .filter((p) => p.weight > 0)
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let k = 0; rest > 0 && order.length > 0; k = (k + 1) % order.length) {
    order[k].value += 1;
    rest -= 1;
  }
  return parts.map((p) => p.value);
}

// ─── Validasi setelan DP di form produk ──────────────────────────────────────
// Audit DP 2026-07 (AUDIT-TAGIHAN-DP.md): ProductForm/QuickProductForm memasang
// `required` + `max={100}` pada input DP, tapi TIDAK ada elemen <form> di kedua
// komponen — tombol simpan memanggil handler langsung, sehingga native form
// validation tak pernah jalan dan kedua atribut itu dekoratif. Akibat nyata di
// produksi: `dp_mode='percent'` tersimpan dengan `dp_amount=100000` (dibaca
// 100.000% = 1.000× harga barang) dan `dp_required=true` tersimpan dengan
// nominal kosong (DP terhitung 0 → nota DP tak pernah terbit, diam-diam).
// Pengaman sesungguhnya karena itu harus di handler simpan — helper di bawah.

/** Batas atas nominal DP mode persen. */
export const DP_MAX_PERCENT = 100;

export type DpDraft = {
  required: boolean;
  mode: DpMode;
  /** Nilai mentah dari input (string, boleh kosong). */
  amount: string;
  /**
   * Harga satuan termahal yang sudah diisi di form (lihat
   * {@link maxDraftUnitPrice}); null/absen = belum ada harga → tak dibandingkan.
   */
  maxUnitPrice?: number | null;
};

/**
 * Harga satuan termahal yang diketik di form produk — pembanding DP per pcs
 * (bughunt G9-7). Per kombinasi: harga dasar (harga varian bila harganya beda
 * per varian, harga seragam bila tidak) + harga tambahan yang berlaku
 * (`priceExtraTextFor`, mig 7900). Tanpa kombinasi = harga seragam saja.
 * Harga dasar kosong / 0 / bukan angka diabaikan; tambahan yang bukan angka
 * dihitung 0. null = belum ada harga (mis. PO harga menyusul).
 *
 * TERMAHAL, bukan termurah: DP per pcs rata di atas harga varian anak itu
 * setelan sah toko sarimbit/famset (PROD 2026-10-22: 105 produk galea +
 * galleryhasanah, mis. DP Rp 100.000 dengan varian Rp 68.000). Yang hampir
 * pasti salah ketik adalah DP di atas varian TERMAHAL (Rp 1.000.000 untuk
 * varian ≤ Rp 350.000) — pembanding termurah menolak menyimpan ke-105 produk.
 * Tambahan wajib ikut: SARIMBIT INFINITY berharga seragam Rp 80.000 dengan
 * tambahan sampai Rp 270.000 per ukuran.
 */
export function maxDraftUnitPrice(input: {
  variablePrice: boolean;
  uniformPrice: string;
  combos: ReadonlyArray<{ price?: string | null; extra?: string | null }>;
}): number | null {
  const rows = input.combos.length
    ? input.combos.map((c) => ({
        base: input.variablePrice ? c.price : input.uniformPrice,
        extra: c.extra
      }))
    : [{ base: input.uniformPrice, extra: null }];
  let max: number | null = null;
  for (const r of rows) {
    const raw = String(r.base ?? "").trim();
    if (!raw) continue;
    const base = Number(raw);
    if (!Number.isFinite(base) || base <= 0) continue;
    const extraNum = Number(String(r.extra ?? "").trim() || 0);
    const n = base + (Number.isFinite(extraNum) && extraNum > 0 ? extraNum : 0);
    if (max == null || n > max) max = n;
  }
  return max;
}

/**
 * Validasi setelan DP sebelum produk disimpan. Mengembalikan pesan error
 * (bahasa admin) atau null bila sah.
 *
 * DP tidak ikut prinsip "boleh menyusul" seperti brand/harga/foto: begitu admin
 * MENYALAKAN toggle "Wajib DP", nominalnya adalah bagian dari pilihan itu.
 * Nominal kosong/0 bukan draf yang menunggu dilengkapi — ia menghasilkan produk
 * yang menagih DP tapi notanya tak pernah terbit, tanpa error di mana pun.
 * Toggle DP dalam keadaan MATI tetap bebas disimpan kapan saja.
 */
export function validateDpDraft(draft: DpDraft): string | null {
  if (!draft.required) return null;

  const raw = draft.amount.trim();
  if (!raw) {
    return draft.mode === "percent"
      ? "Persen DP wajib diisi karena Wajib DP menyala (mis. 30). Matikan Wajib DP bila belum ditentukan."
      : "Jumlah DP per pcs wajib diisi karena Wajib DP menyala (mis. 50000). Matikan Wajib DP bila belum ditentukan.";
  }

  const value = Number(raw);
  if (!Number.isFinite(value)) return "Nominal DP harus berupa angka.";
  if (value <= 0) return "Nominal DP harus lebih besar dari 0.";

  if (draft.mode === "percent" && value > DP_MAX_PERCENT) {
    return `Persen DP maksimal ${DP_MAX_PERCENT}%. Nilai ${raw} terbaca sebagai ${raw}% dari harga barang — bila maksudnya Rp ${raw}, pilih "Nominal / Pcs".`;
  }

  // bughunt G9-7: DP per pcs di atas harga varian TERMAHAL hampir pasti salah
  // ketik (PROD: Rp 1.000.000 per pcs untuk varian ≤ Rp 350.000). Nota DP yang
  // belum dibayar ikut setelan ini, jadi angkanya langsung menular ke tagihan.
  if (
    draft.mode === "per_pcs" &&
    draft.maxUnitPrice != null &&
    draft.maxUnitPrice > 0 &&
    value > draft.maxUnitPrice
  ) {
    return `DP per pcs ${formatRp(value)} melebihi harga varian termahal (${formatRp(draft.maxUnitPrice)}). Periksa nominalnya, mungkin kelebihan satu angka nol.`;
  }

  return null;
}

/**
 * Nilai kolom nominal setelah mode DP diganti.
 *
 * Rupiah dan persen bukan besaran yang bisa saling dipakai: membiarkan "50000"
 * tertinggal saat admin menekan "% dari harga /pcs" mengubah maknanya jadi
 * 50.000% tanpa satu pun tanda di layar — persis asal-usul produk NAFAS PERTAMA
 * (dp_amount 100000 pada mode percent). Karena itu kolom SELALU dikosongkan
 * pada perubahan mode: admin mengetik ulang dalam satuan yang baru. Sengaja
 * tidak memakai heuristik "pertahankan bila nilainya masuk akal" — untuk kolom
 * uang, perilaku yang bisa ditebak lebih berharga daripada hemat satu ketikan.
 */
export function dpAmountAfterModeChange(
  prevMode: DpMode,
  nextMode: DpMode,
  amount: string
): string {
  return prevMode === nextMode ? amount : "";
}

// ─── Rincian nota DP (kartu "Rincian DP" — pembeli & admin) ──────────────────
//
// Nota DP hanya menyimpan SATU baris `manual` berbunyi "DP — <nama produk>"
// beserta nominalnya (0990_dp_invoice_on_po_close.sql · 1260_manual_product_dp.sql).
// Angka itu datang tanpa jejak: pembeli tak bisa membedakan DP yang benar dari
// DP yang salah setelan, tak tahu harga barangnya berapa, dan tak tahu berapa
// sisa yang masih harus dilunasi nanti. Admin pun harus membuka pesanannya di
// layar lain untuk menjawab pertanyaan sesederhana "DP ini 30% dari berapa?".
//
// Rincian di bawah DITURUNKAN ULANG dari pesanan sumber nota (orders →
// order_items + products.dp_mode/dp_amount), BUKAN dari snapshot baru — supaya
// seluruh nota DP yang terlanjur terbit ikut ber-rincian tanpa backfill apa pun.
// Konsekuensinya hitungan ini HIDUP: ia bisa berbeda dari nominal yang tertagih
// bila isi/harga pesanan berubah setelah nota terbit (harga menyusul baru diisi,
// item dibatalkan, nominal diedit admin). Selisih itu TIDAK ditutupi — lihat
// `mismatch`; yang mengikat tetap nominal TERTAGIH (`billedDp`), karena itulah
// yang dibayar dan yang jadi kredit DP saat pelunasan.

/** Label baku untuk item yang harganya belum ditentukan. */
export const DP_UNPRICED_LABEL = "Harga menyusul";

function formatRp(n: number): string {
  return `Rp ${Math.round(n).toLocaleString("id-ID")}`;
}

/**
 * Persen tanpa nol ekor: 30 → "30", 12,5 → "12,5".
 *
 * Presisi 6 desimal, bukan 2: `dp_amount` bertipe `numeric` dan
 * `validateDpDraft` menerima pecahan apa pun ≤ 100, jadi membulatkan tampilan
 * ke 2 desimal membuat rumus di layar tak lagi menghasilkan nominal di
 * bawahnya (12,345% ditulis "12,35%" → 400.000 × 12,35% = 49.400, sedangkan
 * yang ditagih 49.380). Rumus yang tak bisa dihitung ulang pembacanya persis
 * hal yang kartu ini ada untuk menghilangkan.
 */
function formatPct(n: number): string {
  return n.toLocaleString("id-ID", { maximumFractionDigits: 6 });
}

export type DpBreakdownItemInput = {
  variantName: string;
  qty: number;
  /** order_items.unit_price — null = harga menyusul. */
  unitPrice: number | null;
  /**
   * order_items.keep_status. Item 'cancelled' DIKELUARKAN dari hitungan —
   * cermin `compute_order_dp_total` (`keep_status is distinct from 'cancelled'`),
   * jadi keep_status NULL tetap ikut dihitung.
   */
  keepStatus?: string | null;
  /**
   * Pcs baris ini yang sudah ditagih di tagihan barang hidup (mig 8000). DP
   * dihitung dari sisanya; absen = 0 (perilaku lama).
   */
  billedQty?: number | null;
  /**
   * order_items.gift_role — 'hadiah' tak ber-DP (mig 8680, H11): barisnya tetap
   * didaftar tapi tak masuk hitungan DP. Absen = bukan hadiah (nota publik lama).
   */
  giftRole?: string | null;
};

export type DpBreakdownInput = {
  productName: string;
  dpRequired: boolean;
  dpMode: DpMode | null | undefined;
  dpAmount: number | null | undefined;
  items: ReadonlyArray<DpBreakdownItemInput>;
  /** Nominal DP yang BENAR-BENAR tertagih = total baris aktif nota DP. */
  billedDp: number;
  /**
   * Nota DP TERKUNCI ({@link dpInvoiceLocked}) — nominalnya tak lagi mengikuti
   * tagihan barang. Absen/false = nota masih mengikuti (perilaku mig 8000).
   * Wajib diisi setiap pemanggil yang memegang notanya (bughunt G8-7): tanpa
   * itu DP lunas yang pcs-nya sudah ditagih terbaca "× 0 pcs = Rp 0".
   */
  dpLocked?: boolean;
};

/**
 * Nota DP TERKUNCI: nominalnya tak lagi mengikuti tagihan barang (mig 8000).
 * Kebalikan persis predikat hidup `reconcile_open_dp_with_goods` — server hanya
 * menyusulkan nota DP berstatus open/partial yang uang bersihnya (saldo +
 * transfer − refund) ≤ 0 DAN tanpa bukti bayar pending. Sengaja bukan sekadar
 * `status === 'paid'`: DP ber-uang sebagian atau ber-bukti pending juga dikunci
 * server, begitu pula nota yang sudah void. Status tak dikenal → terkunci
 * (dasar hitungan lama, semua pcs aktif).
 */
export function dpInvoiceLocked(inv: {
  status: string | null | undefined;
  balanceApplied?: number | null;
  amountPaid?: number | null;
  refundedAmount?: number | null;
  hasPendingPayment?: boolean | null;
}): boolean {
  if (inv.status !== "open" && inv.status !== "partial") return true;
  const n = (v: number | null | undefined) =>
    v != null && Number.isFinite(v) ? v : 0;
  if (n(inv.balanceApplied) + n(inv.amountPaid) - n(inv.refundedAmount) > 0) return true;
  return Boolean(inv.hasPendingPayment);
}

/**
 * Dasar hitungan DP di kartu Rincian DP: `open` = pcs yang BELUM ditagih di
 * tagihan barang (`computeOrderDpDue`, mig 8000) · `all` = semua pcs aktif
 * (`computeOrderDpTotal`, hitungan sebelum mig 8000).
 */
export type DpBasis = "open" | "all";

export type DpBreakdownRow = {
  variantName: string;
  qty: number;
  /** null = harga menyusul. */
  unitPrice: number | null;
  /** qty × unitPrice; null bila harga menyusul. */
  lineTotal: number | null;
  cancelled: boolean;
  /** Baris HADIAH pembelian (mig 8680) — didaftar, tak ber-DP. */
  gift: boolean;
  /** Pcs yang sudah ditagih di tagihan barang hidup (mig 8000), 0..qty. */
  billedQty: number;
  /**
   * Bagian baris ini dari DP yang ditagihkan (`allocateDpPaidByRow` atas dasar
   * `dpBasis`) — Σ semua baris = `billedDp`. null = tanpa setelan DP (nominal
   * diketik admin), atau baris batal / hadiah / tanpa pcs ber-DP.
   */
  dpShare: number | null;
};

export type DpBreakdown = {
  productName: string;
  /** Semua item pesanan, termasuk yang dibatalkan (ditandai `cancelled`). */
  rows: DpBreakdownRow[];
  /** Σ qty item AKTIF. */
  activeQty: number;
  /** Σ pcs item aktif yang sudah ditagih di tagihan barang hidup (mig 8000). */
  billedQty: number;
  /** Σ pcs item aktif yang BELUM ditagih — dasar DP sejak mig 8000. */
  openQty: number;
  /** Σ (qty × harga) item aktif yang sudah punya harga. */
  pricedSubtotal: number;
  /** Σ qty item aktif yang harganya belum ada. */
  unpricedQty: number;
  hasUnpricedItem: boolean;
  /**
   * Item "harga menyusul" tidak ikut menambah DP. HANYA benar untuk mode
   * `percent` (harga null berkontribusi 0) — pada `per_pcs` item itu TETAP
   * menambah DP lewat qty-nya, dan pada flat DP tak bergantung item sama
   * sekali. Dipisahkan sebagai flag supaya kartu tak perlu menebak ulang
   * semantik mode dan salah memberi tahu pembeli.
   */
  unpricedExcludedFromDp: boolean;
  /** Harga produk bisa dipastikan penuh (ada item aktif & semuanya ber-harga). */
  subtotalIsComplete: boolean;
  hasCancelledItem: boolean;
  /**
   * Setelan DP produk: "30% dari harga produk" / "Rp 50.000 per pcs" /
   * "Rp 200.000 per pesanan". null = nota DP tanpa setelan produk (produk
   * manual mig 126 — nominalnya diketik admin).
   */
  settingLabel: string | null;
  /** Aritmetikanya: "30% × Rp 400.000" / "Rp 50.000 × 5 pcs". null utk flat/manual. */
  formulaLabel: string | null;
  /** Nota DP terkunci (input `dpLocked`) — nominalnya tak mengikuti tagihan barang. */
  dpLocked: boolean;
  /**
   * Dasar `computedDp`/`formulaLabel`. Nota yang masih mengikuti tagihan barang
   * selalu `open`. Nota terkunci `all`, KECUALI nominalnya justru cocok dengan DP
   * pcs yang belum ditagih dan tidak dengan semua pcs — nota DP yang terbit
   * sesudah sebagian pcs ditagih (mig 8000) lalu dibayar: ia memang menagih pcs
   * sisa itu, dan "× semua pcs" di sampingnya adalah selisih palsu.
   * Batas diketahui: nota seperti itu yang pcs sisanya KEMUDIAN ikut ditagih tak
   * cocok dengan dasar mana pun (pcs sisa kini 0) → jatuh ke `all` dan terbaca
   * selisih. Menutupnya butuh jumlah pcs yang sudah tertagih SAAT nota dikunci
   * (tindak lanjut G8-7, perlu data server), bukan tebakan cocok-nominal lagi.
   */
  dpBasis: DpBasis;
  /**
   * DP menurut setelan & isi pesanan SAAT INI atas dasar `dpBasis` — pcs yang
   * belum ditagih (`computeOrderDpDue`) atau semua pcs aktif
   * (`computeOrderDpTotal`); null bila tanpa setelan DP.
   */
  computedDp: number | null;
  billedDp: number;
  /** Hitungan sekarang tak lagi sama dengan yang tertagih. */
  mismatch: boolean;
  /**
   * Setelan DP produknya sendiri yang keliru: persen di atas 100% — nilai per-pcs
   * yang tertinggal saat mode diganti ("Rp 100.000" terbaca 100.000% = 1.000×
   * harga barang, akar bug AUDIT-TAGIHAN-DP; dijaga `validateDpDraft` sejak
   * 2026-07, tapi produk lama yang terlanjur tersimpan masih ada di PROD).
   * Rincian ini membuatnya kelihatan alih-alih hanya memunculkan nominal raksasa.
   */
  settingLooksWrong: boolean;
  /**
   * Sebab `settingLooksWrong` (bughunt G9-7): persen > 100, atau DP per pcs
   * melebihi harga pcs TERMAHAL di pesanan ini (DP lebih besar dari harga
   * setiap pcs-nya). Opsional supaya rincian buatan tangan (tes, dokumen lama)
   * tetap sah.
   */
  settingWrongReason?: "percent" | "per_pcs" | null;
  /** Harga satuan termahal item aktif berharga — pembanding DP per pcs. */
  priciestUnitPrice?: number | null;
  /**
   * Perkiraan sisa pelunasan barang setelah DP (harga pcs yang BELUM ditagih −
   * DP tertagih; pcs yang sudah ditagih dilunasi lewat tagihan barangnya).
   * null bila masih ada harga menyusul / tak ada item aktif — angka setengah
   * benar di kolom uang lebih menyesatkan daripada tidak ada angka. null juga
   * untuk nota DP TERKUNCI yang pcs-nya sudah ada yang ditagih: DP yang sudah
   * dibayar terpakai sebagai Kredit DP di tagihan barang, jadi sisa yang
   * sebenarnya tertera di tagihan barang itu (bughunt G8-7).
   */
  remainingAfterDp: number | null;
};

/**
 * Susun rincian nota DP dari pesanan sumbernya. Pure → diuji `dp.test.ts`.
 *
 * Hitungan DP-nya sengaja memakai {@link computeOrderDpTotal} yang SAMA dengan
 * seluruh app (cermin SQL `compute_order_dp_total`) dan hanya disuapi item
 * AKTIF — kalau item dibatalkan ikut disuapkan, angka "hitungan sekarang" akan
 * mengklaim selisih yang tidak pernah ada di server.
 */
export function buildDpBreakdown(input: DpBreakdownInput): DpBreakdown {
  const rows: DpBreakdownRow[] = input.items.map((it) => {
    const qty = Number.isFinite(it.qty) ? Math.max(0, Math.trunc(it.qty)) : 0;
    const unitPrice =
      it.unitPrice != null && Number.isFinite(it.unitPrice)
        ? Math.max(0, it.unitPrice)
        : null;
    const billed =
      it.billedQty != null && Number.isFinite(it.billedQty)
        ? Math.min(qty, Math.max(0, Math.trunc(it.billedQty)))
        : 0;
    return {
      variantName: (it.variantName ?? "").trim() || "Varian",
      qty,
      unitPrice,
      lineTotal: unitPrice != null ? unitPrice * qty : null,
      cancelled: it.keepStatus === "cancelled",
      gift: it.giftRole === "hadiah",
      billedQty: billed,
      dpShare: null as number | null
    };
  });

  // Dasar hitungan DP: baris aktif BUKAN hadiah — cermin `compute_order_dp_total`
  // (mig 8680, H11). Dulu hadiah ikut, jadi DP per pcs terbaca "× 6 pcs" untuk
  // nota yang server hitung 5 pcs, plus catatan selisih palsu.
  const active = rows.filter((r) => !r.cancelled && !r.gift);
  const activeQty = active.reduce((s, r) => s + r.qty, 0);
  const billedQty = active.reduce((s, r) => s + r.billedQty, 0);
  const openQty = activeQty - billedQty;
  const pricedSubtotal = active.reduce((s, r) => s + (r.lineTotal ?? 0), 0);
  // Harga pcs yang belum ditagih — dasar DP persen & sisa pelunasan (mig 8000).
  const openPricedSubtotal = active.reduce(
    (s, r) => s + (r.unitPrice != null ? r.unitPrice * (r.qty - r.billedQty) : 0),
    0
  );
  const unpricedQty = active.reduce(
    (s, r) => s + (r.unitPrice == null ? r.qty : 0),
    0
  );
  const hasUnpricedItem = unpricedQty > 0;
  const pricedUnits = active
    .map((r) => r.unitPrice)
    .filter((p): p is number => p != null && p > 0);
  // Termahal, bukan termurah: DP per pcs rata di atas harga pcs anak itu setelan
  // sah toko sarimbit (lihat `maxDraftUnitPrice`); yang janggal = DP di atas
  // harga SETIAP pcs pesanan.
  const priciestUnitPrice = pricedUnits.length ? Math.max(...pricedUnits) : null;

  const mode: DpMode = input.dpMode ?? "order";
  const value =
    Number.isFinite(input.dpAmount ?? NaN)
      ? Math.max(0, input.dpAmount as number)
      : 0;
  // Tanpa setelan DP di produk (produk manual: dp_required=false, dp_amount 0)
  // tidak ada rumus untuk ditunjukkan — nominalnya memang diketik admin.
  const hasSetting = input.dpRequired && value > 0;

  const billedDp = Number.isFinite(input.billedDp)
    ? Math.max(0, Math.round(input.billedDp))
    : 0;
  // Toleransi 1 rupiah yang sama dengan `mismatch` di bawah.
  const matchesBilled = (dp: number | null) =>
    dp != null && Math.abs(Math.round(dp) - billedDp) <= 1;

  const dpLines = active.map((r) => ({
    qty: r.qty,
    unitPrice: r.unitPrice,
    billedQty: r.billedQty
  }));
  const dueDp = hasSetting ? computeOrderDpDue(mode, value, dpLines) : null;
  const totalDp = hasSetting ? computeOrderDpTotal(mode, value, dpLines) : null;

  // Nota DP yang masih mengikuti tagihan barang dihitung dari pcs yang belum
  // ditagih (mig 8000). Nota TERKUNCI (bughunt G8-7) tidak: server tak lagi
  // menyusulkannya, jadi DP lunas yang pcs-nya kemudian ditagih tetap menagih
  // semua pcs — dihitung dari pcs sisa, ia terbaca "× 0 pcs = Rp 0" plus catatan
  // selisih palsu. Pengecualian: nominal terkunci yang memang DP pcs sisa (nota
  // terbit sesudah sebagian pcs ditagih, lalu dibayar) tetap berdasar pcs sisa.
  const locked = Boolean(input.dpLocked);
  const dpBasis: DpBasis =
    !locked || (billedQty > 0 && !matchesBilled(totalDp) && matchesBilled(dueDp))
      ? "open"
      : "all";
  const computedDp = dpBasis === "open" ? dueDp : totalDp;

  // Toleransi 1 rupiah: server menghitung persen di `numeric` presisi-penuh,
  // klien di double — pada persen pecahan keduanya bisa beda 1 rupiah tanpa
  // ada yang salah. Menyalakan peringatan untuk selisih itu = mengajari
  // pembeli mengabaikan peringatan yang seharusnya berarti.
  const mismatch = computedDp != null && Math.abs(Math.round(computedDp) - billedDp) > 1;

  // DP per baris (putusan pemilik 2026-10-29): DP yang ditagihkan dibagi ke
  // baris menurut setelan produk, atas dasar yang SAMA dengan rumusnya — pcs
  // yang sudah masuk tagihan barang tak ber-DP bila dasarnya `open`. Tanpa
  // setelan (nominal diketik admin) tak ada aturan untuk membaginya. Saat
  // nominal tertagih tak lagi sama dengan hitungan isi pesanan (`mismatch`:
  // pcs ditambah/dibatalkan sesudah DP dibayar, semua harga masih menyusul)
  // pembagiannya juga tak dicetak — "DP Rp 21.428" di samping "Rp 10.000 per
  // pcs" hanya terbaca salah hitung.
  if (hasSetting && !mismatch) {
    const shares = allocateDpPaidByRow(
      mode,
      billedDp,
      rows.map((r, i) => ({
        id: String(i),
        qty: dpBasis === "open" ? r.qty - r.billedQty : r.qty,
        unitPrice: r.unitPrice,
        keepStatus: r.cancelled ? "cancelled" : null,
        giftRole: r.gift ? "hadiah" : null
      }))
    );
    rows.forEach((r, i) => {
      r.dpShare = shares.get(String(i)) ?? null;
    });
  }

  // Pcs yang sudah ditagih di tagihan barang tak lagi ber-DP (mig 8000) — rumusnya
  // menyebut dasar yang benar-benar dipakai, bukan isi pesanan seluruhnya.
  const openBasis = dpBasis === "open";
  const openTail = openBasis && billedQty > 0 ? " belum ditagih" : "";
  const basisSubtotal = openBasis ? openPricedSubtotal : pricedSubtotal;
  const basisQty = openBasis ? openQty : activeQty;
  let settingLabel: string | null = null;
  let formulaLabel: string | null = null;
  if (hasSetting) {
    if (mode === "percent") {
      settingLabel = `${formatPct(value)}% dari harga produk`;
      formulaLabel = `${formatPct(value)}% × ${formatRp(basisSubtotal)}${openTail}`;
    } else if (mode === "per_pcs") {
      settingLabel = `${formatRp(value)} per pcs`;
      formulaLabel = `${formatRp(value)} × ${basisQty} pcs${openTail}`;
    } else {
      // Flat per pesanan: nominalnya sudah jawaban lengkap, tak ada perkalian
      // untuk ditampilkan — memaksakan "Rp X × 1" hanya menambah kebisingan.
      settingLabel = `${formatRp(value)} per pesanan (flat)`;
    }
  }

  // Setelan yang janggal: persen > 100 (nominal tertinggal saat ganti mode),
  // atau DP per pcs di atas harga pcs termahal (bughunt G9-7 — salah ketik nol).
  const settingWrongReason: "percent" | "per_pcs" | null = !hasSetting
    ? null
    : mode === "percent" && value > DP_MAX_PERCENT
      ? "percent"
      : mode === "per_pcs" && priciestUnitPrice != null && value > priciestUnitPrice
        ? "per_pcs"
        : null;

  return {
    productName: (input.productName ?? "").trim(),
    rows,
    activeQty,
    billedQty,
    openQty,
    pricedSubtotal,
    unpricedQty,
    hasUnpricedItem,
    unpricedExcludedFromDp: hasUnpricedItem && hasSetting && mode === "percent",
    subtotalIsComplete: activeQty > 0 && !hasUnpricedItem,
    hasCancelledItem: rows.some((r) => r.cancelled),
    settingLabel,
    formulaLabel,
    dpLocked: locked,
    dpBasis,
    computedDp,
    billedDp,
    mismatch,
    settingLooksWrong: settingWrongReason != null,
    settingWrongReason,
    priciestUnitPrice,
    // Nota terkunci tanpa pcs tertagih: harga pcs sisa = harga semua pcs, jadi
    // rumusnya sama. Begitu ada pcs tertagih, Kredit DP-nya sudah terpakai di
    // tagihan barang — "harga sisa − DP" mengurangkan DP yang sama dua kali.
    // Berlaku juga di cabang pengecualian (dasar `open`): Kredit DP bisa sudah
    // menempel di tagihan barang pcs yang tertagih lebih dulu, karena Edit
    // Tagihan / Tambah Barang menghitung ulang kredit DP lunas di nota terbuka
    // (`admin_edit_open_invoice`, `admin_add_invoice_items`). Klien tak tahu
    // kredit itu sudah terpakai atau belum, jadi angkanya tak ditebak.
    remainingAfterDp:
      activeQty > 0 && !hasUnpricedItem && !(locked && billedQty > 0)
        ? Math.max(0, openPricedSubtotal - billedDp)
        : null
  };
}

// ─── Bagian kartu Rincian DP per konteks tampil ─────────────────────────────

export type DpBreakdownContext = {
  /**
   * Nota DP HANGUS (void karena tagihan barang terbit sebelum DP dibayar).
   * Tak sepeser pun DP dibayar, jadi tak ada potongan apa pun yang lahir
   * darinya — setiap kalimat tentang "potongan saat pelunasan" jadi keliru.
   */
  hangus?: boolean;
  /**
   * Disematkan di halaman TAGIHAN BARANG. Tagihan itu sendiri sudah memuat
   * sisa yang sebenarnya; "Perkiraan sisa pelunasan" di dalamnya = angka
   * ketiga yang tak cocok dengan tagihan mana pun (terutama tagihan pecahan).
   */
  embedded?: boolean;
};

export type DpBreakdownSections = {
  showRemaining: boolean;
  showMismatchNote: boolean;
  /**
   * Baris rumus DP. Disembunyikan pada nota HANGUS yang hitungan hidupnya sudah
   * tak sama: sejak mig 8000 pcs yang ikut ditagih di tagihan barang tak ber-DP
   * lagi, jadi rumus "× 0 pcs" di samping nominal yang dulu ditagih hanya terbaca
   * seperti salah hitung.
   */
  showFormula: boolean;
  /**
   * Catatan "N pcs sudah ditagih di tagihan barang, jadi DP hanya dihitung dari
   * pcs yang belum ditagih" (mig 8000). Mati bila dasar hitungannya semua pcs
   * (nota DP terkunci, bughunt G8-7) — kalimatnya tak lagi benar.
   */
  showBilledNote: boolean;
  /** Label baris nominal: DP hangus tidak lagi "ditagihkan". */
  billedLabel: string;
  /** Catatan penutup kartu. */
  closingNote: "credit" | "hangus";
};

/**
 * Bagian mana dari kartu Rincian DP yang tampil. Pure supaya aturan "nota
 * hangus tak pernah menjanjikan potongan" terkunci tes, bukan cuma JSX.
 */
export function dpBreakdownSections(
  b: Pick<DpBreakdown, "remainingAfterDp" | "settingLooksWrong" | "mismatch" | "computedDp"> &
    Partial<Pick<DpBreakdown, "billedQty" | "dpBasis">>,
  ctx: DpBreakdownContext = {}
): DpBreakdownSections {
  const hangus = Boolean(ctx.hangus);
  return {
    showRemaining:
      !hangus &&
      !ctx.embedded &&
      b.remainingAfterDp != null &&
      !b.settingLooksWrong,
    // Catatan selisih bicara soal "DP yang benar-benar dibayar jadi potongan"
    // — tak relevan untuk nota yang tak pernah dibayar.
    showMismatchNote: !hangus && b.mismatch && b.computedDp != null,
    showFormula: !(hangus && b.mismatch),
    showBilledNote: !hangus && b.dpBasis !== "all" && (b.billedQty ?? 0) > 0,
    billedLabel: hangus ? "DP (ikut ditagih di tagihan barang)" : "DP yang ditagihkan",
    closingNote: hangus ? "hangus" : "credit"
  };
}

// ─── Kelayakan terbitkan nota DP (cermin guard server) ───────────────────────

export type DpIssueContext = {
  dpRequired: boolean;
  productType: "preorder" | "ready_stock";
  /** products.status (lifecycle PO). */
  productStatus: string;
  /** Status nota DP aktif order ini; null bila belum ada. */
  dpInvoiceStatus: string | null;
  /**
   * DP yang akan terhitung server sekarang — sejak mig 8000 `compute_order_dp_due`
   * (pcs yang belum masuk tagihan barang hidup; {@link computeOrderDpDue}).
   */
  dpAmount: number | null;
  /**
   * Nota DP pesanan ini hangus (mig 6590) tanpa nota aktif pengganti DAN
   * penyebabnya MASIH menagih pesanan ini (`judgeHangusCause`, cermin
   * `hangus_dp_cause_bills_order` mig 7100). DP hangus yang penyebabnya sudah
   * dibatalkan / tak lagi memuat pesanan ini ("yatim") BUKAN `dpHangus`:
   * tombolnya justru jalur pemulihannya (bughunt G7-12).
   */
  dpHangus?: boolean;
  /**
   * Nota DP pesanan ini KEDALUWARSA (mig 7130). Keterangan saja — TIDAK
   * memblokir sendirian (mig 7140): pesanan yang kembali punya baris Belum
   * Ready (mis. Tambah Produk) dibuka kembali notanya oleh server, dan tombol
   * Terbitkan Nota DP adalah jalur pemulihannya.
   */
  dpExpired?: boolean;
  /**
   * SEMUA barang pesanan sudah ready (`orderGoodsAllReady`, cermin
   * `order_goods_all_ready` mig 7130) — keadaan SAAT INI, bukan riwayat nota.
   * Server tak menerbitkan DP untuk barang yang sudah ada.
   */
  allGoodsReady?: boolean;
  /**
   * SEMUA pcs aktif pesanan sudah ditagih di tagihan barang hidup (mig 8000).
   * Server mencatat DP-nya "ikut ditagih" di sana, bukan nota DP yang menagih.
   */
  allPcsBilled?: boolean;
};

/** Satu baris pesanan untuk `orderGoodsAllReady`; `string | null` = bentuk lama (keep saja). */
export type GoodsReadyRow =
  | string
  | null
  | undefined
  | {
      keepStatus: string | null | undefined;
      /** `order_items.ready_at` (mig 7040) — NULL pada baris paket = barang belum datang. */
      readyAt?: string | null;
    };

/**
 * Cermin klien `order_goods_all_ready` (mig 7130, diperluas mig 7760) atas
 * SELURUH baris pesanan: minimal satu baris Sudah Ready / pengiriman
 * (`unpaid_keep`, `paid_keep`, `hold`, `shipped`, atau `requested_shipment`
 * yang barangnya sudah ada) DAN tak ada baris Belum Ready — `keep_status`
 * kosong, ATAU baris paket yang masih menunggu barang (`requested_shipment`
 * ber-`ready_at` kosong, mig 7760). Baris batal / gagal produksi tak dihitung.
 * Wajib diberi semua baris pesanan, bukan hanya yang tampil di satu kolom.
 * Bentuk lama (daftar `keep_status`) tetap diterima: baris paket tanpa
 * `ready_at` dibaca seperti dulu (ready) — pemanggil yang ingin aturan 7760
 * wajib membawa `readyAt`.
 */
export function orderGoodsAllReady(rows: ReadonlyArray<GoodsReadyRow>): boolean {
  let anyReady = false;
  for (const r of rows) {
    const ks = typeof r === "object" && r !== null ? r.keepStatus : r;
    if (ks == null) return false;
    if (ks === "cancelled") continue;
    if (
      ks === "requested_shipment" &&
      typeof r === "object" &&
      r !== null &&
      "readyAt" in r &&
      r.readyAt == null
    ) {
      return false;
    }
    anyReady = true;
  }
  return anyReady;
}

/**
 * Alasan nota DP belum bisa diterbitkan manual, atau null bila boleh.
 *
 * Cermin guard `issue_dp_invoice_internal` (mig 099) supaya tombol admin tidak
 * memanggil RPC yang pasti no-op: produk preorder yang masih OPEN PO ditolak
 * (`return null`), begitu pula DP terhitung ≤ 0 — dan justru diamnya server
 * pada kasus kedua itulah yang membuat 13 pesanan kehilangan nota DP tanpa
 * jejak. Di UI, keduanya jadi pesan yang bisa ditindaklanjuti.
 */
export function dpIssueBlockReason(ctx: DpIssueContext): string | null {
  if (!ctx.dpRequired) return "Produk ini tidak memakai DP.";
  if (ctx.dpInvoiceStatus != null) return "Tagihan DP sudah ada.";
  // Selama tagihan barang penyebabnya masih menagih, menerbitkan ulang hanya
  // memulangkan nota hangus yang sama (server idempoten, mig 6590) — tombolnya
  // tak boleh menjanjikan nota baru. Penyebab yang tak lagi menagih ("yatim")
  // tidak sampai ke sini: server (mig 7030) membuka kembali nota DP lamanya,
  // atau memindahkannya ke tagihan barang hidup yang kini menagih pesanan itu.
  // mig 8000: tagihan barang yang menagih SEBAGIAN pesanan tak lagi menahan DP
  // hangus — selama masih ada DP sisa (pcs belum ditagih) yang barangnya belum
  // ready semua, server membuka kembali nota DP-nya bernominal sisa itu, jadi
  // tombolnya justru jalur pemulihannya. Cermin kotak Keranjang admin
  // (`dpBilledElsewhere`, AdminCartClient).
  if (
    ctx.dpHangus &&
    !(ctx.dpAmount != null && ctx.dpAmount > 0 && !ctx.allGoodsReady)
  ) {
    // Bukan "lewat Bayar DP": opsi itu mati begitu tagihan barangnya menerima
    // uang, dan tagihan yang sudah lunas tak menagih apa pun lagi (bughunt
    // 2026-10-13) — kalimat ini benar di ketiga keadaan.
    return "Nominal DP pesanan ini sudah ikut ditagih di tagihan barang yang masih menagihnya — dibayar lewat tagihan barang itu, bukan lewat tagihan DP baru.";
  }
  // mig 7130: DP tak berlaku untuk barang yang sudah ready semua — server
  // memulangkan nota kedaluwarsa (atau tak menerbitkan apa pun). Penanda
  // kedaluwarsa SENDIRIAN tak memblokir (mig 7140): pesanan yang kembali punya
  // baris Belum Ready menerbitkan ulang nota DP yang sama lewat tombol ini.
  if (ctx.allGoodsReady) {
    return "Barang pesanan ini sudah ready semua — DP tidak berlaku lagi (kedaluwarsa). Tagih harga barangnya lewat tagihan barang.";
  }

  const status = ctx.productStatus.trim().toUpperCase();
  if (ctx.productType === "preorder" && status === "OPEN PO") {
    return "Tagihan DP terbit otomatis saat PO ditutup.";
  }

  // mig 8000: DP sisa nol karena seluruh pcs sudah ditagih — bukan setelan yang
  // kurang, jadi jangan menyuruh admin melengkapi nominal DP.
  if (ctx.allPcsBilled && !(ctx.dpAmount != null && ctx.dpAmount > 0)) {
    return "Semua pcs pesanan ini sudah ditagih di tagihan barang — DP-nya ikut ditagih di sana, tak perlu tagihan DP.";
  }

  if (!ctx.dpAmount || ctx.dpAmount <= 0) {
    return "DP terhitung Rp 0 — lengkapi nominal DP di produk (dan harga item bila DP memakai mode persen) sebelum menerbitkan tagihan.";
  }

  return null;
}

/** Nota DP layak diterbitkan manual oleh admin. */
export function canIssueDpInvoice(ctx: DpIssueContext): boolean {
  return dpIssueBlockReason(ctx) === null;
}

// ─── Rincian DP untuk pesan WhatsApp ──────────────────────────────────────────
// Nota DP hanya berisi SATU baris bernominal ("DP — Gamis Salwa: Rp 450.000"),
// jadi rekap nota yang dibagikan ke WhatsApp memuat angka tanpa jejak: pembeli
// tak bisa menilai DP-nya benar atau salah setelan, tak tahu harga barangnya
// berapa, dan tak tahu sisa yang masih harus dilunasi.
//
// Blok di bawah adalah versi TEKS dari kartu "Rincian DP" di layar — bahan &
// aturannya SATU (`buildDpBreakdown`), jadi angka yang diperdebatkan lewat
// WhatsApp tak bisa berbeda dari yang dibuka pembeli di aplikasi.

/**
 * Blok teks "Rincian DP" siap tempel ke pesan WhatsApp. Mengembalikan array
 * baris (pemanggil yang menyusun jarak antar-blok); `[]` bila tak ada yang
 * layak diceritakan — nota DP produk manual tanpa item sumber sama sekali.
 *
 * Nada catatan mengikuti `audience`, PERSIS seperti kartunya: temuan "setelan
 * DP produk keliru" adalah pekerjaan admin; pembeli hanya perlu tahu agar
 * mengkonfirmasi sebelum membayar.
 */
export function buildDpShareLines(
  b: DpBreakdown,
  opts: { audience?: "buyer" | "admin" } = {}
): string[] {
  const audience = opts.audience ?? "buyer";
  const out: string[] = [];
  const title = b.productName.trim();
  out.push(`*Rincian DP${title ? ` — ${title}` : ""}*`);

  // 1 — Harga produk asli per varian. Item "harga menyusul" TETAP disebut:
  // menyembunyikannya membuat daftar terbaca lebih pendek dari pesanannya.
  for (const r of b.rows) {
    const value =
      r.unitPrice == null ? DP_UNPRICED_LABEL : formatRp(r.lineTotal ?? 0);
    const unit =
      r.unitPrice != null && r.qty > 1
        ? ` (${formatRp(r.unitPrice)} × ${r.qty})`
        : "";
    // mig 8000: pcs yang sudah masuk tagihan barang tak ber-DP lagi — disebut,
    // supaya rumus "× N pcs belum ditagih" di bawah bisa dihitung ulang pembaca.
    const tail = r.cancelled
      ? " (dibatalkan)"
      : r.gift
        ? " (hadiah, tanpa DP)"
        : r.billedQty > 0
          ? r.billedQty >= r.qty
            ? " (sudah ditagih)"
            : ` (${r.billedQty} pcs sudah ditagih)`
          : "";
    // DP per baris — Σ-nya = "DP yang ditagihkan" di bawah (`dpShare`).
    const dp = r.dpShare != null && r.dpShare > 0 ? ` · DP ${formatRp(r.dpShare)}` : "";
    out.push(`• ${r.variantName} ×${r.qty} — ${value}${unit}${dp}${tail}`);
  }

  const qtyLabel = b.activeQty > 0 ? ` (${b.activeQty} pcs)` : "";
  out.push(
    `Subtotal harga produk${qtyLabel}: ${
      b.hasUnpricedItem && b.pricedSubtotal === 0
        ? DP_UNPRICED_LABEL
        : formatRp(b.pricedSubtotal)
    }`
  );

  // 2 & 3 — Setelan DP dan aritmetika yang menghasilkan nominal tertagih.
  out.push(
    b.settingLabel
      ? `Setelan DP produk: ${b.settingLabel}`
      : "Sumber nominal DP: ditetapkan admin"
  );
  if (b.formulaLabel) {
    // Saat nota tak lagi sama dengan hitungan hidup, rumusnya diberi label
    // "hitungan saat ini" DAN hasilnya ditulis — kalau tidak, "30% × Rp
    // 800.000" tepat di atas nominal lain terbaca seperti salah hitung.
    out.push(
      b.mismatch && b.computedDp != null
        ? `Hitungan saat ini: ${b.formulaLabel} = ${formatRp(b.computedDp)}`
        : `Perhitungan: ${b.formulaLabel}`
    );
  }
  out.push(`*DP yang ditagihkan: ${formatRp(b.billedDp)}*`);

  // 4 — Sisa pelunasan. Disembunyikan saat setelan DP-nya sendiri keliru: DP
  // yang membengkak membuat sisanya ter-clamp jadi "Rp 0" — terbaca sebagai
  // "tak ada lagi yang perlu dibayar", kebalikan dari keadaan sebenarnya.
  if (b.remainingAfterDp != null && !b.settingLooksWrong) {
    out.push(
      `Perkiraan sisa pelunasan barang: ${formatRp(b.remainingAfterDp)}`
    );
  }

  // Catatan — hanya yang benar-benar mengubah cara membaca angka di atas.
  if (b.hasUnpricedItem) {
    out.push(
      `Catatan: ${b.unpricedQty} pcs belum ada harganya (${DP_UNPRICED_LABEL.toLowerCase()}), jadi belum masuk subtotal${
        b.unpricedExcludedFromDp
          ? " dan belum ikut dihitung dalam DP"
          : b.formulaLabel
            ? " — tapi DP-nya dihitung per pcs, jadi pcs itu SUDAH termasuk dalam DP di atas"
            : ""
      }. Sisa pelunasan baru bisa dipastikan setelah harganya ditetapkan admin.`
    );
  }
  if (b.settingLooksWrong) {
    out.push(
      audience === "admin"
        ? b.settingWrongReason === "per_pcs" && b.priciestUnitPrice != null
          ? `Catatan: setelan DP produk tertulis ${b.settingLabel}, lebih besar dari harga pcs termahal di pesanan ini (${formatRp(b.priciestUnitPrice)}). Perbaiki setelan DP di produknya lalu terbitkan ulang tagihannya.`
          : `Catatan: setelan DP produk tertulis ${b.settingLabel} — di atas 100%, hampir pasti nominal rupiah yang tertinggal saat mode DP diganti ke persen. Perbaiki setelan DP di produknya lalu terbitkan ulang tagihannya.`
        : `Catatan: nominal DP pada tagihan ini tampak tidak wajar (setelannya tertulis ${b.settingLabel}). Mohon konfirmasi dulu sebelum membayar.`
    );
  }
  if (b.mismatch && b.computedDp != null) {
    // JANGAN menjanjikan selisihnya dikreditkan otomatis: kredit DP saat
    // pelunasan = DP yang BENAR-BENAR DIBAYAR pada nota ini (SQL
    // 0720_invoice_acc_allocation_rpc.sql), bukan hitungan hidup. Jangan pula
    // menulis "dikunci saat nota diterbitkan": nota DP yang belum tersentuh
    // uang IKUT pcs pesanan & setelan DP produk (mig 8450, bughunt G9 #2b) —
    // selisih yang tersisa = nota yang sudah dibayar/disunting.
    out.push(
      `Catatan: nominal DP di tagihan ini berbeda dari hitungan isi pesanan saat ini (${formatRp(
        b.computedDp
      )}${
        b.hasCancelledItem ? ", ada item yang dibatalkan" : ""
      }). Hubungi admin bila selisihnya perlu disesuaikan.`
    );
  }
  if (!b.settingLabel) {
    out.push(
      "Catatan: DP ini dicatat manual oleh admin, bukan dari setelan DP produk."
    );
  }
  out.push(
    "DP yang sudah dibayar otomatis jadi potongan (Kredit DP) di tagihan barang saat pelunasan."
  );

  return out;
}
